/** Wersja angielska — typowana względem polskiej (brak klucza = błąd kompilacji). */
import { fleetRoutesPl, type Shape } from './fleet-routes-pl';

export const fleetRoutesEn: Shape<typeof fleetRoutesPl> = {
  routes: {
    title: 'Planned routes',
    description: 'Routes from the Routimo planner (ERP) and from CSV file imports compared with mileage from logbooks. Actual km come from odometer readings, never from the plan.',
    sourceNote: 'Route sources: the Routimo planner in the ERP database (erp:logistics import) and CSV file imports.',
    sourceNoteLink: 'Format description and import',
    chipRoutes: 'Routes',
    chipCompared: 'Compared',
    chipAbove: 'Above plan',
    chipNoActual: 'Without actual',
    search: 'Search by route name or identifier…',
    empty: 'No routes in the selected period. Import a CSV file to see them.',
    emptyFiltered: 'No route matches the filters.',
    emptyImport: 'Go to import',
    loadFailed: 'Could not load routes.',
    filters: {
      from: 'From',
      to: 'To',
      vehicle: 'Vehicle',
      allVehicles: 'All vehicles',
      driver: 'Driver',
      allDrivers: 'All drivers',
      status: 'Route status',
      allStatuses: 'All statuses',
      compare: 'Plan vs actual',
      allCompare: 'Any result',
      logbook: 'Logbook',
      allLogbook: 'With and without',
      linked: 'With a linked logbook',
      unlinked: 'Without a linked logbook',
      unmapped: 'Without vehicle only',
      unapproved: 'Include unapproved logbooks',
      cancelled: 'Show cancelled',
    },
    status: { PLANNED: 'Planned', IN_PROGRESS: 'In progress', COMPLETED: 'Completed', CANCELLED: 'Cancelled' },
    plan: {
      OK: 'Within plan',
      ABOVE_PLAN: 'Above plan',
      BELOW_PLAN: 'Below plan',
      NO_PLAN: 'No planned km',
      NO_ACTUAL: 'No actual',
      NO_DATA: 'No data',
    },
    planHelp: {
      OK: 'The deviation is within the fleet tolerance ({tolerance}%).',
      ABOVE_PLAN: 'Logbook mileage exceeds the plan by more than the tolerance ({tolerance}%).',
      BELOW_PLAN: 'Logbook mileage is below the plan by more than the tolerance ({tolerance}%).',
      NO_PLAN: 'The route system did not provide planned km — nothing to compare against.',
      NO_ACTUAL: 'No linked logbook with mileage (or the logbook is not approved).',
      NO_DATA: 'Neither planned km nor logbook mileage is available.',
    },
    summary: {
      title: 'Plan vs actual',
      routes: 'Routes',
      compared: 'Compared',
      within: 'Within plan',
      above: 'Above plan',
      below: 'Below plan',
      noPlan: 'Without planned km',
      noActual: 'Without actual',
      plannedKm: 'Planned km',
      actualKm: 'Actual km',
      tolerance: 'Tolerance: {tolerance}% (fleet settings)',
      mode: { APPROVED_ONLY: 'Only approved logbooks count as actual.', INCLUDING_UNAPPROVED: 'Submitted and inconsistent logbooks also count as actual (preliminary result).' },
    },
    col: { date: 'Date', route: 'Route', status: 'Status', vehicle: 'Vehicle', driver: 'Driver', stops: 'Stops', planned: 'Planned km', actual: 'Actual km', deviation: 'Deviation', result: 'Plan vs actual', logbooks: 'Logbooks' },
    noVehicle: 'no vehicle',
    noDriver: 'no driver',
    externalValue: 'from file: {value}',
    noLogbook: 'no logbook',
    autoLink: 'Link automatically',
    autoLinkHint: 'Links a route to a logbook only when exactly one unlinked logbook of that vehicle exists on the route day.',
    autoLinkDone: 'Linked {linked}, ambiguous {ambiguous}, no candidate {none} ({routes} routes analysed).',
    detail: {
      back: 'Back to routes',
      loadFailed: 'Could not load the route.',
      notFound: 'The route does not exist or you have no access to it.',
      source: 'Source',
      sourceCsv: 'CSV file import',
      sourceRoutimo: 'Routimo route planner (ERP)',
      externalId: 'Identifier in the source system',
      date: 'Route date',
      window: 'Planned window',
      lastRun: 'Last import',
      comparison: 'Plan vs actual',
      plannedKm: 'Planned km',
      actualKm: 'Actual km (logbooks)',
      deviationKm: 'Deviation',
      deviationPct: 'Deviation %',
      tolerance: 'Tolerance',
      stops: 'Delivery stops',
      stopsEmpty: 'The route has no delivery stops in the file.',
      stopCol: { seq: 'No.', id: 'Stop id', name: 'Name', address: 'Address', planned: 'Planned time' },
      logbooks: 'Linked logbooks',
      logbooksEmpty: 'No linked logbooks.',
      logbookKm: '{km} km',
      noKm: 'no mileage',
      unlink: 'Unlink',
      unlinkDone: 'Logbook unlinked from the route.',
      candidates: 'Logbooks to link',
      candidatesHint: 'Logbooks of this vehicle from the route day not yet linked to any route.',
      candidatesEmpty: 'No unlinked logbooks of this vehicle from the route day.',
      candidatesNoVehicle: 'The route has no vehicle assigned — complete the identifier mapping in the integration.',
      link: 'Link',
      linkDone: 'Logbook linked to the route.',
      openLogbook: 'Logbook',
      openVehicle: 'Vehicle page',
      mapIds: 'Complete the mapping',
      unmappedVehicle: 'The vehicle “{value}” from the file is not mapped.',
      unmappedDriver: 'The driver “{value}” from the file is not mapped.',
    },
  },
  integration: {
    title: 'Route integration',
    description: 'Import of planned routes from a CSV file, run log and identifier mapping for vehicles and drivers.',
    banner: {
      title: 'Data source: CSV file import',
      body: 'We do not know the name or API of the route system yet, so nothing is fetched automatically — routes enter Fleet only from an uploaded file. Logbooks, fuel entries and analytics work without this integration. Once the route system is identified, this screen will show its synchronisations with no changes on your side.',
    },
    tabs: { import: 'Import', runs: 'Runs', idmap: 'Identifier mapping', format: 'File format' },
    import: {
      title: 'CSV file import',
      description: 'First check the file in preview mode (nothing is saved), then import. Importing the same route again does not create a duplicate.',
      choose: 'Choose a CSV file',
      drop: 'CSV file (UTF-8 or Windows-1250), up to 5 MB',
      selected: 'Selected file: {name} ({size})',
      clear: 'Clear selection',
      preview: 'Preview (no saving)',
      run: 'Import',
      confirmTitle: 'Import the file?',
      confirmBody: 'Routes from the file will be saved or updated. A route with even one invalid row is skipped entirely.',
      problem: { extension: 'Choose a file with the .csv extension.', tooLarge: 'The file is larger than 5 MB.', empty: 'The file is empty.' },
      resultPreview: 'Preview result — nothing was saved',
      resultImport: 'Import result',
      failedTitle: 'The import failed',
      failedRun: 'See the run in the log',
      doneImport: 'Import finished.',
      donePreview: 'Preview ready — nothing was saved.',
      importAfterPreview: 'Import this file',
      missingColumns: 'Details: {details}',
    },
    report: {
      status: 'Status',
      file: 'File',
      startedAt: 'Started',
      finishedAt: 'Finished',
      triggeredBy: 'Run by',
      attempts: 'Attempts',
      dryRun: 'Preview (no saving)',
      rowsTotal: 'Rows',
      rowsRejected: 'Rejected rows',
      routesCreated: 'New routes',
      routesUpdated: 'Updated',
      routesUnchanged: 'Unchanged',
      routesUnmapped: 'Without vehicle',
      errors: 'Per-row errors',
      errorsNone: 'No row errors.',
      errorsTruncated: 'The first 200 errors are shown — the rest were omitted from the report.',
      errCol: { row: 'Row', column: 'Column', code: 'Code', message: 'Description' },
      unmapped: 'Unmapped identifiers',
      unmappedHint: 'These values from the file match no vehicle or driver. Routes were saved without them — nothing is guessed.',
      unmappedNone: 'All identifiers were matched.',
      unmappedCol: { kind: 'Kind', value: 'Value from file', routes: 'Routes', action: 'Action' },
      mapNow: 'Map',
      kind: { VEHICLE: 'Vehicle', DRIVER: 'Driver' },
      runStatus: { RUNNING: 'Running', SUCCESS: 'Success', PARTIAL: 'Partial', FAILED: 'Failed' },
      errorCodes: {
        ROUTE_ID_REQUIRED: 'Route identifier missing',
        ROUTE_DATE_INVALID: 'Invalid route date',
        STOP_SEQUENCE_INVALID: 'Invalid stop number',
        STOP_SEQUENCE_DUPLICATE: 'Duplicate stop number',
        PLANNED_KM_INVALID: 'Invalid planned km',
        DATETIME_INVALID: 'Invalid date or time',
        STATUS_INVALID: 'Unknown route status',
        ROUTE_FIELD_CONFLICT: 'Conflicting values within one route',
        ROW_TOO_LONG: 'Row or value too long',
        ROUTE_SKIPPED: 'Route skipped entirely',
      },
    },
    runs: {
      title: 'Run log',
      description: 'Every import (including previews) leaves an entry with counters and errors.',
      empty: 'No runs — the import has not been run yet.',
      filterStatus: 'Run status',
      allStatuses: 'All statuses',
      loadFailed: 'Could not load the log.',
      col: { started: 'Started', file: 'File', status: 'Status', rows: 'Rows', rejected: 'Rejected', created: 'New', updated: 'Updated', unchanged: 'Unchanged', unmapped: 'Without vehicle', by: 'Run by' },
      detailTitle: 'Synchronisation run',
      back: 'Back to integration',
      notFound: 'The run does not exist or you have no access to it.',
      loadDetailFailed: 'Could not load the run.',
      runError: 'Error message',
      source: 'Source: {source}',
    },
    idmap: {
      title: 'Identifier mapping',
      description: 'Links a value from the file (e.g. a vehicle number in the route system) to a Fleet vehicle or driver. A change links existing routes immediately.',
      add: 'Add mapping',
      empty: 'No mappings. Matching by plate number and driver email works without them.',
      loadFailed: 'Could not load mappings.',
      filterKind: 'Kind',
      allKinds: 'Vehicles and drivers',
      col: { kind: 'Kind', external: 'Value from file', target: 'Assigned to', source: 'Source', action: 'Action' },
      remove: 'Remove mapping',
      removeTitle: 'Remove the mapping?',
      removeBody: 'Routes linked by this mapping will lose their vehicle or driver and the value will return to the unmapped list.',
      removeDone: 'Mapping removed. Unlinked routes: {count}.',
      dialogTitle: 'New mapping',
      dialogDescription: 'Enter the value exactly as it appears in the file (case and surrounding spaces are ignored).',
      kind: 'Kind',
      external: 'Value from file',
      externalRequired: 'Enter the value from the file.',
      pickVehicle: 'Fleet vehicle',
      pickDriver: 'Fleet driver',
      targetRequired: 'Choose a vehicle or a driver.',
      choose: 'Choose…',
      driversNeedRight: 'The driver list requires the right to edit vehicles.',
      saved: 'Mapping saved. Linked routes: {count}.',
      relink: 'Re-link routes',
      relinkHint: 'Recomputes routes without a vehicle or driver, e.g. after adding a vehicle to the fleet.',
      relinkDone: 'Routes re-linked: {count}.',
      unmappedRoutes: 'Routes without a vehicle',
      unmappedRoutesHint: 'Routes whose vehicle was not recognised — complete the mapping or add the vehicle to the fleet.',
      unmappedRoutesEmpty: 'Every route has a vehicle assigned.',
      unmappedOpen: 'See routes',
    },
    format: {
      title: 'CSV file format',
      intro: 'One row is one delivery stop of a route (or the route itself when stop columns are empty). The header is required; column order is arbitrary and unknown columns are ignored.',
      rules: {
        encoding: 'Encoding: UTF-8 (with or without BOM); a file saved from a Polish Excel (Windows-1250) is read too.',
        separator: 'Separator: semicolon, comma or tab — detected from the header. Wrap values containing a semicolon or newline in quotes.',
        dates: 'Dates: YYYY-MM-DD or DD.MM.YYYY. Times: ISO 8601 with a zone, or local time without a zone (treated as Europe/Warsaw).',
        idempotent: 'The route key is its identifier: importing again creates no duplicates and unchanged routes are skipped.',
        skipped: 'A route with even one invalid row is skipped entirely, so a correct route is not overwritten with a truncated stop list.',
        match: 'Vehicle: mapping → plate number. Driver: mapping → email of an account with the Driver role. No match = route without a vehicle / driver.',
      },
      colName: 'Column',
      colRequired: 'Required',
      colMeaning: 'Meaning',
      required: 'yes',
      optional: 'no',
      example: 'Example (separator “;”)',
      copy: 'Copy example',
      copied: 'Example copied.',
      download: 'Download example',
      columns: {
        route_id: 'Route identifier in the source system (up to 500 characters) — the idempotency key.',
        route_date: 'Route date: YYYY-MM-DD or DD.MM.YYYY.',
        status: 'PLANNED (default), IN_PROGRESS, COMPLETED or CANCELLED.',
        vehicle_plate: 'Plate number — matched to a fleet vehicle (ignoring case, spaces and dashes).',
        vehicle_external_id: 'Vehicle identifier in the route system — mapped; takes precedence over the plate number.',
        driver_email: 'Email of an active account with the Driver role.',
        driver_external_id: 'Driver identifier in the route system — mapped.',
        planned_km: 'Planned route km: a number with a dot or comma, 0–5000.',
        planned_start: 'Planned route start.',
        planned_end: 'Planned route end.',
        stop_sequence: 'Stop sequence number (1–10000), unique within the route; required when stop data is present.',
        stop_id: 'Delivery stop identifier.',
        stop_name: 'Delivery stop name.',
        stop_address: 'Delivery stop address.',
        stop_planned_at: 'Planned delivery time at the stop.',
      },
    },
  },
};
